import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useCreateRecipeIngredient, useGetRecipeIngredient } from "../../../api/recipe-ingredients";
import { useGetStaticRecipeIngredientData } from "../../../api/static-data";
import { errorView, successMessage } from "../../../helpers/ErrorHandler";
import { CompanyRow } from "../companyList/types/companyListView.types";
import AllowListModal from "../shared/components/AllowListModal";

interface RecipeIngredientModalProps {
  company: CompanyRow | null;
  closeModal: () => void;
}

/**
 * Tailwind rebuild of the legacy `RecipeIngredient` modal
 * (src/screens/company/recipeIngredient/RecipeIngredient.tsx): same
 * queries/mutation, same case-insensitive name search, same "at least one
 * ingredient" guard and same success message — rendered through the shared
 * `AllowListModal`. Open whenever `company` is set.
 */
const RecipeIngredientModal = ({ company, closeModal }: RecipeIngredientModalProps) => {
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<Array<string | number>>([]);

  const { data: recipeData } = useGetRecipeIngredient(company?.tenantId as string);
  const { data: staticIngredients } = useGetStaticRecipeIngredientData();
  const { isLoading: isSaving, mutate: createRecipeIngredient } = useCreateRecipeIngredient();

  useEffect(() => {
    setChecked(recipeData?.map((item: any) => item?.ingredientId) || []);
  }, [recipeData]);

  const items = useMemo(() => {
    const re = new RegExp(_.escapeRegExp(search), "i");
    return (staticIngredients || [])
      .filter((res: any) => re.test(res?.text))
      .map((item: any) => ({
        value: item.value,
        text: item.text,
        createdAt: recipeData?.find((recipe: any) => recipe.ingredientId === item.value)?.createdAt
      }));
  }, [search, staticIngredients, recipeData]);

  const toggle = (value: string | number) =>
    setChecked(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));

  const confirm = () => {
    if (checked.length === 0) {
      errorView("Please select at least one ingredient");
      return;
    }
    createRecipeIngredient(
      { ingredientIds: checked, tenantId: company?.tenantId },
      {
        onSuccess: () => {
          closeModal();
          successMessage("Recipe add and remove successfully");
        }
      }
    );
  };

  return (
    <AllowListModal
      visibleModal={!!company}
      title={`Add Ingredients for ${company?.name ?? ""}`}
      nameColumnLabel="Ingredient Name"
      searchPlaceholder="Search ingredients"
      items={items}
      checkedValues={checked}
      onToggle={toggle}
      searchValue={search}
      onSearchChange={setSearch}
      isSaving={isSaving}
      onConfirm={confirm}
      onClose={closeModal}
    />
  );
};

export default RecipeIngredientModal;
